<script setup lang="ts">
import { ref, watch } from "vue";
import APIClient from "@/APIClient";
import { useToast } from "@/composable/useToast";
import { useTranslation } from "i18next-vue";
import DRBtn from "@/components/UI/DRBtn.vue";

enum FeedbackType {
  BUG = "bug",
  IDEA = "idea",
}

const types = [FeedbackType.IDEA, FeedbackType.BUG];

const props = defineProps<{
  modelValue: boolean;
  view?: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const { t } = useTranslation();
const { showSuccess, showDanger } = useToast();

const loading = ref(false);
const form = ref({
  type: "",
  message: "",
});

// Reset form when modal closes
watch(
  () => props.modelValue,
  (visible) => {
    if (!visible) {
      form.value = { type: "", message: "" };
    }
  }
);

const close = () => {
  emit("update:modelValue", false);
};

const sendFeedback = async () => {
  if (!form.value.message.trim() || !form.value.type) {
    showDanger(t("feedback.bot.dialog.validationError"));
    return;
  }

  loading.value = true;
  try {
    await APIClient.sendFeedback({
      view: props.view || "general",
      type: form.value.type,
      message: form.value.message,
    });
    showSuccess(t("feedback.bot.dialog.successMessage"));
    close();
  } catch {
    showDanger(t("feedback.bot.dialog.errorMessage"));
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <el-dialog
    :model-value="modelValue"
    :title="$t('feedback.bot.dialog.title')"
    width="400px"
    destroy-on-close
    @update:model-value="emit('update:modelValue', $event)"
  >
    <el-form :model="form" label-position="top">
      <el-form-item :label="$t('feedback.bot.dialog.typeLabel')">
        <el-select
          v-model="form.type"
          :placeholder="$t('feedback.bot.dialog.selectPlaceholder')"
        >
          <el-option
            v-for="type in types"
            :key="type"
            :label="$t(`feedback.bot.dialog.type.${type}`)"
            :value="type"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="$t('feedback.bot.dialog.messageLabel')">
        <el-input
          v-model="form.message"
          type="textarea"
          :rows="4"
          :placeholder="$t('feedback.bot.dialog.messagePlaceholder')"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <DRBtn
        variant="outline"
        class="me-2"
        :label="$t('cancel')"
        @click="close"
      />
      <DRBtn
        variant="primary"
        :label="$t('send')"
        :loading="loading"
        @click="sendFeedback"
      />
    </template>
  </el-dialog>
</template>
